import { Button, Card, CardContent } from '@h2hspark/ui';
import { Share, Smartphone } from 'lucide-react';
import * as React from 'react';

import { isRunningInstalled, useInstallPrompt } from './install-prompt';
import {
  installMechanism,
  IOS_PUSH_NEEDS_INSTALL,
  isAppleTouchPlatform,
} from './supported-platforms';

/**
 * The one place the captured install offer is surfaced (`MEM-040` AC-4, `MEM-043` AC-1, AC-7).
 *
 * ## Why a settings row and not a banner
 *
 * `MEM-040` AC-4 is "captured rather than surfaced **automatically**". The event is caught in
 * `install-prompt.ts` so the browser's own bar never fires; what remains is choosing a moment to
 * offer it, and that moment is `PLT-008` AC-5's — once after a member's first completed
 * registration, at most twice, never once installed — which needs a per-member record this story
 * does not own.
 *
 * A control the member navigated to needs no such record. Somebody on My Account looking for
 * "add to home screen" is not being ambushed, so this is the surface that can exist today without
 * pre-empting `PLT-008`. The capture is the part that will not change when `PLT-008` lands.
 *
 * ## Three states, because there are three platforms' worth of truth
 *
 *   - **Installed** — nothing. A control that reinstalls an installed app is a puzzle.
 *   - **`prompt`** — a button that raises the real dialogue, from the member's own tap.
 *   - **`manual-ios`** — Safari fires no event, so the honest thing is the instruction. This is
 *     the same call `MEM-031` AC-1 makes about the scanner on a laptop: explain rather than
 *     approximate. A greyed-out Install button on an iPhone would tell a member the feature is
 *     broken when it is one Share menu away.
 *
 * Everywhere else the card renders nothing rather than guessing, which is what "supported
 * platforms follow the `SYS` matrix" means for a member on a browser nobody has ruled on yet.
 */
export function InstallAppCard(): React.ReactElement | null {
  const { canInstall, promptInstall } = useInstallPrompt();
  const [busy, setBusy] = React.useState(false);

  const mechanism = installMechanism({
    canPrompt: canInstall,
    alreadyInstalled: isRunningInstalled(),
    appleTouch: typeof navigator === 'undefined' ? false : isAppleTouchPlatform(navigator),
  });

  if (mechanism === 'none') return null;

  return (
    <Card>
      <CardContent className="space-y-3 pt-5">
        <h2 className="text-2xs font-brand font-semibold uppercase tracking-caps text-muted-foreground">
          {INSTALL_HEADING}
        </h2>
        {mechanism === 'prompt' ? (
          <>
            <p className="text-sm text-muted-foreground">{INSTALL_BODY}</p>
            <Button
              type="button"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void promptInstall().finally(() => {
                  setBusy(false);
                });
              }}
            >
              <Smartphone className="mr-2 h-4 w-4" aria-hidden />
              {INSTALL_ACTION}
            </Button>
          </>
        ) : (
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <Share className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
            <span>{INSTALL_IOS_INSTRUCTION}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export const INSTALL_HEADING = 'Add to your home screen';
/** Says what installing buys, because "install the app" on a website is a request with no reason. */
export const INSTALL_BODY =
  'H2HSpark opens full screen from your home screen, and it still opens when the venue wi-fi does not.';
export const INSTALL_ACTION = 'Add to home screen';
/**
 * The iOS instruction. Names the menu and the item, because a member who cannot find Share gives
 * up — and on iOS this is also what unlocks push (`PLT-008` AC-6).
 */
export const INSTALL_IOS_INSTRUCTION =
  `In Safari, tap the Share button and choose “Add to Home Screen”. ${IOS_PUSH_NEEDS_INSTALL}`;
